import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { services } from '../data/services';
import phoneIcon from '../icons/phone_icon.png';
import whatsappIcon from '../icons/whatsapp_icon.png';

const AnimatedCounter: React.FC<{ end: number; suffix?: string; duration?: number }> = ({ end, suffix = '', duration = 2000 }) => {
    const [count, setCount] = useState(0);
    const [isVisible, setIsVisible] = useState(false);
    const elementRef = React.useRef<HTMLDivElement>(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.1 }
        );

        if (elementRef.current) {
            observer.observe(elementRef.current);
        }

        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (!isVisible) return;

        let startTime: number | null = null;
        let animationFrame: number;

        const animate = (timestamp: number) => {
            if (!startTime) startTime = timestamp;
            const progress = timestamp - startTime;
            const percentage = Math.min(progress / duration, 1);

            setCount(Math.floor(percentage * end));

            if (percentage < 1) {
                animationFrame = requestAnimationFrame(animate);
            } else {
                setCount(end);
            }
        };

        animationFrame = requestAnimationFrame(animate);
        return () => cancelAnimationFrame(animationFrame);
    }, [isVisible, end, duration]);

    return <div ref={elementRef}>{count.toLocaleString()}{suffix}</div>;
};

const ServiceDetail: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const navigate = useNavigate();
    const service = services.find(s => s.slug === slug);

    useEffect(() => {
        window.scrollTo(0, 0);
        if (service) {
            document.title = `${service.title} | Cheapest Car Recovery UK`;
        }
    }, [service]);

    if (!service) {
        return (
            <div style={{
                padding: '100px 20px',
                textAlign: 'center',
                fontFamily: "'Inter', sans-serif",
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                background: '#f8f9fa'
            }}>
                <h1 style={{ fontSize: '48px', color: '#333', marginBottom: '20px' }}>404</h1>
                <p style={{ fontSize: '18px', color: '#666', marginBottom: '30px' }}>Sorry, the service you are looking for does not exist.</p>
                <Link to="/" style={{
                    backgroundColor: '#FFF222',
                    color: '#333',
                    padding: '15px 30px',
                    borderRadius: '50px',
                    textDecoration: 'none',
                    fontWeight: 'bold',
                    boxShadow: '0 4px 15px rgba(255, 242, 34, 0.3)',
                    transition: 'all 0.3s ease'
                }}>
                    Back to Homepage
                </Link>
            </div>
        );
    }

    const styles = `
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;800&display=swap');

    :root {
      --primary: #FFF222;
      --secondary: #333;
      --accent: #25D366;
      --text: #1a1a1a;
      --text-light: #666;
      --bg: #ffffff;
      --section-bg: #fdfdfd;
    }

    .detail-wrapper {
      font-family: 'Inter', sans-serif;
      color: var(--text);
      background: var(--bg);
      line-height: 1.6;
    }

    /* Hero Section */
    .hero-container {
      position: relative;
      height: 60vh;
      min-height: 400px;
      overflow: hidden;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
    }

    .hero-bg {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-size: cover;
      background-position: center;
      transition: transform 0.5s ease;
      z-index: 1;
    }

    .hero-overlay {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.8) 100%);
      z-index: 2;
    }

    .hero-content {
      position: relative;
      z-index: 3;
      max-width: 900px;
      padding: 0 20px;
      text-align: center;
      animation: fadeInUp 0.8s ease-out;
    }

    .hero-title {
      font-size: clamp(32px, 8vw, 56px);
      font-weight: 800;
      margin-bottom: 20px;
      text-transform: uppercase;
      letter-spacing: -1px;
      line-height: 1.1;
    }

    .hero-badge {
      display: inline-block;
      background: var(--primary);
      color: var(--secondary);
      padding: 8px 16px;
      border-radius: 50px;
      font-weight: 700;
      font-size: 14px;
      margin-bottom: 20px;
      text-transform: uppercase;
    }

    /* Main Content */
    .main-content {
      max-width: 1200px;
      margin: -60px auto 60px;
      padding: 0 20px;
      position: relative;
      z-index: 10;
    }

    .info-card {
      background: white;
      border-radius: 24px;
      padding: 50px;
      box-shadow: 0 20px 40px rgba(0,0,0,0.08);
      margin-bottom: 40px;
    }

    .content-grid {
      display: grid;
      grid-template-columns: 1.6fr 1fr;
      gap: 60px;
    }

    .section-title {
      font-size: 32px;
      font-weight: 800;
      margin-bottom: 25px;
      position: relative;
      padding-bottom: 15px;
      color: var(--secondary);
    }

    .section-title::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: 60px;
      height: 4px;
      background: var(--primary);
    }

    .text-block {
      font-size: 18px;
      color: var(--text-light);
      margin-bottom: 30px;
    }

    /* Process Section */
    .process-container {
        margin-top: 60px;
        background: #fdfdfd;
        padding: 40px;
        border-radius: 20px;
    }

    .process-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
        gap: 30px;
        margin-top: 30px;
    }

    .process-item {
        position: relative;
        padding: 20px;
        background: white;
        border-radius: 15px;
        box-shadow: 0 10px 20px rgba(0,0,0,0.03);
    }

    .process-number {
        font-size: 40px;
        font-weight: 900;
        color: var(--primary);
        opacity: 0.3;
        position: absolute;
        top: 10px;
        right: 20px;
    }

    .process-step-title {
        font-size: 20px;
        font-weight: 700;
        margin-bottom: 10px;
        position: relative;
        z-index: 2;
    }

    .process-step-detail {
        font-size: 16px;
        color: var(--text-light);
    }

    /* FAQ Section */
    .faq-section {
        margin-top: 60px;
    }

    .faq-item {
        margin-bottom: 20px;
        border-bottom: 1px solid #eee;
        padding-bottom: 20px;
    }

    .faq-question {
        font-size: 20px;
        font-weight: 700;
        margin-bottom: 10px;
        color: var(--secondary);
        display: flex;
        gap: 10px;
    }
    
    .faq-question::before {
        content: 'Q:';
        color: var(--primary);
    }

    .faq-answer {
        font-size: 16px;
        color: var(--text-light);
        padding-left: 30px;
    }

    /* Side Panel */
    .side-panel {
      display: flex;
      flex-direction: column;
      gap: 30px;
    }

    .cta-box {
      background: #f8f9fa;
      padding: 30px;
      border-radius: 20px;
      border: 1px solid #eee;
      position: sticky;
      top: 100px;
    }

    .cta-box h3 {
      font-size: 20px;
      font-weight: 700;
      margin-bottom: 20px;
    }

    .feature-list {
      list-style: none;
      padding: 0;
      margin: 0 0 30px 0;
    }

    .feature-list li {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-bottom: 12px;
      font-weight: 600;
      color: var(--secondary);
    }

    .feature-icon {
      color: var(--primary);
      font-weight: 900;
      font-size: 20px;
    }

    /* Buttons */
    .btn-container {
      display: flex;
      flex-direction: column;
      gap: 15px;
    }

    .btn {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 12px;
      padding: 16px 24px;
      border-radius: 12px;
      font-weight: 700;
      font-size: 16px;
      text-decoration: none;
      transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
      border: none;
      cursor: pointer;
    }

    .btn:hover {
      transform: translateY(-5px);
    }

    .btn-call {
      background: var(--primary);
      color: var(--secondary);
      box-shadow: 0 10px 20px rgba(255, 242, 34, 0.2);
    }

    .btn-whatsapp {
      background: var(--accent);
      color: white;
      box-shadow: 0 10px 20px rgba(37, 211, 102, 0.2);
    }

    .btn-icon-img {
      width: 20px;
      height: 20px;
    }

    /* Animations */
    @keyframes fadeInUp {
      from { opacity: 0; transform: translateY(30px); }
      to { opacity: 1; transform: translateY(0); }
    }

    /* Floating Header */
    .top-nav {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      padding: 20px 40px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      z-index: 100;
    }

    .logo-link {
        display: flex;
        align-items: center;
        gap: 10px;
        text-decoration: none;
        color: white;
    }

    .logo-img {
        height: 40px;
        filter: brightness(0) invert(1);
    }

    .back-btn {
        background: rgba(255,255,255,0.2);
        backdrop-filter: blur(10px);
        color: white;
        padding: 10px 20px;
        border-radius: 50px;
        text-decoration: none;
        font-weight: 600;
        font-size: 14px;
        transition: all 0.3s ease;
        display: flex;
        align-items: center;
        gap: 8px;
        border: 1px solid rgba(255,255,255,0.1);
    }

    .back-btn svg {
        width: 20px;
        height: 20px;
        transition: transform 0.3s ease;
    }

    .back-btn:hover {
        background: white;
        color: black;
    }

    .back-btn:hover svg {
        transform: translateX(-3px);
    }

    .back-icon-only {
        display: none;
    }

    @media (max-width: 768px) {
        .back-btn {
            padding: 10px;
            border-radius: 12px;
            width: 40px;
            height: 40px;
            justify-content: center;
        }
        .back-btn span {
            display: none;
        }
        .back-icon-only {
            display: block;
        }
    }

    /* Floating Action Buttons */
    .floating-buttons {
      position: fixed !important;
      bottom: 20px !important;
      right: 20px !important;
      display: flex !important;
      flex-direction: column !important;
      gap: 15px !important;
      z-index: 1000 !important;
    }

    .floating-btn {
      width: 60px !important;
      height: 60px !important;
      border-radius: 50% !important;
      border: none !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      font-size: 24px !important;
      color: white !important;
      text-decoration: none !important;
      box-shadow: 0 4px 20px rgba(0,0,0,0.3) !important;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
      cursor: pointer !important;
    }

    .floating-btn:hover {
        transform: scale(1.1) translateY(-2px) !important;
        box-shadow: 0 6px 25px rgba(0,0,0,0.4) !important;
    }

    .call-btn {
        background: linear-gradient(135deg, #FFF222, #FFE55C) !important;
        border: 2px solid #333 !important;
    }

    .whatsapp-btn {
        background: linear-gradient(135deg, #25D366, #25D366) !important;
        border: 2px solid #333 !important;
    }

    .floating-icon {
        width: 28px !important;
        height: 28px !important;
    }

    .call-btn .floating-icon {
        filter: brightness(0) !important;
    }

    .whatsapp-btn .floating-icon {
        filter: invert(100%) sepia(100%) saturate(0%) 
        hue-rotate(288deg) brightness(102%) contrast(102%) !important;
    }

    .floating-btn-text {
        position: absolute !important;
        right: 70px !important;
        background: rgba(0,0,0,0.8) !important;
        color: white !important;
        padding: 8px 12px !important;
        border-radius: 20px !important;
        font-size: 12px !important;
        white-space: nowrap !important;
        opacity: 0 !important;
        transform: translateX(10px) !important;
        transition: all 0.3s ease !important;
        pointer-events: none !important;
    }

    .floating-btn:hover .floating-btn-text {
        opacity: 1 !important;
        transform: translateX(0) !important;
    }

    /* Mobile Responsive */
    @media (max-width: 992px) {
      .hero-container {
        height: 50vh;
      }
      .content-grid {
        grid-template-columns: 1fr;
        gap: 40px;
      }
      .info-card {
        padding: 30px;
        border-radius: 0;
        box-shadow: none;
        margin: 0;
      }
      .main-content {
          margin-top: 0;
          padding: 0;
      }
      .top-nav {
          padding: 15px 20px;
      }
      .logo-text {
          display: none;
      }
      .cta-box {
          position: static;
      }
      .process-container {
          padding: 20px;
          margin-top: 40px;
      }
    }
    
    @media (min-width: 769px) {
      .floating-buttons {
          bottom: 30px !important;
          right: 30px !important;
      }
      .floating-btn {
          width: 65px !important;
          height: 65px !important;
          font-size: 26px !important;
      }
      .floating-btn-text {
          right: 75px !important;
          padding: 10px 15px !important;
          font-size: 14px !important;
      }
    }
    .hero-call-btn {
      display: none;
      background: var(--primary);
      color: var(--secondary);
      padding: 12px 30px;
      border-radius: 50px;
      text-decoration: none;
      font-weight: 800;
      font-size: 16px;
      margin-top: 20px;
      box-shadow: 0 4px 15px rgba(255, 242, 34, 0.4);
      transition: all 0.3s ease;
      text-transform: uppercase;
    }

    @media (max-width: 768px) {
      .hero-call-btn {
        display: inline-block;
      }
    }
  `;

    return (
        <div className="detail-wrapper">
            <style>{styles}</style>

            {/* Dynamic Header */}
            <nav className="top-nav">
                <Link to="/" className="logo-link">
                    <img src="/my-logo.png" alt="Logo" className="logo-img" />
                    <span className="logo-text" style={{ fontWeight: 800 }}>CHEAPEST CAR RECOVERY UK</span>
                </Link>
                <button onClick={() => navigate(-1)} className="back-btn" aria-label="Go Back">
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M19 12H5M12 19l-7-7 7-7" />
                    </svg>
                    <span>Go Back</span>
                </button>
            </nav>

            {/* Hero Section */}
            <section className="hero-container">
                <div className="hero-bg" style={{ backgroundImage: `url(${service.image})` }}></div>
                <div className="hero-overlay"></div>
                <div className="hero-content">
                    <div className="hero-badge">Professional Recovery Service</div>
                    <h1 className="hero-title">{service.title}</h1>
                    <p style={{ opacity: 0.9, fontSize: '18px', fontWeight: 500 }}>
                        Reliable 24/7 Car Recovery and Roadside Assistance Across UK
                    </p>
                    <a href="tel:+447432387241" className="hero-call-btn">
                        Call Now
                    </a>
                </div>
            </section>

            {/* Main Content */}
            <main className="main-content">
                <div className="info-card">
                    <div className="content-grid">
                        <div className="service-story">
                            <h2 className="section-title">Overview</h2>
                            <div className="text-block">
                                {service.longDescription.split('\n').map((line, i) => {
                                    // Header detection (### Header)
                                    if (line.trim().startsWith('###')) {
                                        return <h3 key={i} style={{ fontSize: '24px', fontWeight: 'bold', margin: '30px 0 15px 0', color: '#333' }}>{line.replace('###', '').trim()}</h3>;
                                    }

                                    // List item detection (• or -)
                                    if (line.trim().startsWith('•') || line.trim().startsWith('-')) {
                                        return (
                                            <div key={i} style={{ display: 'flex', gap: '10px', marginBottom: '10px', paddingLeft: '20px' }}>
                                                <span style={{ color: '#FFF222', fontWeight: 'bold' }}>•</span>
                                                <span>{line.replace(/^[•-]\s*/, '').trim()}</span>
                                            </div>
                                        );
                                    }

                                    // Empty line check
                                    if (!line.trim()) return <br key={i} />;

                                    // Regular paragraph with bold support
                                    const parts = line.split(/(\*\*.*?\*\*)/g);
                                    return (
                                        <p key={i} style={{ marginBottom: '20px' }}>
                                            {parts.map((part, index) => {
                                                if (part.startsWith('**') && part.endsWith('**')) {
                                                    return <strong key={index}>{part.slice(2, -2)}</strong>;
                                                }
                                                return part;
                                            })}
                                        </p>
                                    );
                                })}
                            </div>

                            <div style={{ background: '#FFF22210', padding: '25px', borderRadius: '15px', borderLeft: '5px solid #FFF222', marginBottom: '40px' }}>
                                <p style={{ margin: 0, fontWeight: 700, fontStyle: 'italic', fontSize: '18px' }}>
                                    "Our goal is to reach you within 45 minutes, no matter where you are in the UK. We provide the cheapest and most professional service you can find."
                                </p>
                            </div>

                            <div className="process-container">
                                <h2 className="section-title">How It Works</h2>
                                <div className="process-grid">
                                    {service.process.map((p, i) => (
                                        <div key={i} className="process-item">
                                            <div className="process-number">0{i + 1}</div>
                                            <h3 className="process-step-title">{p.step}</h3>
                                            <p className="process-step-detail">{p.detail}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="faq-section">
                                <h2 className="section-title">Frequently Asked Questions</h2>
                                {service.faqs.map((faq, i) => (
                                    <div key={i} className="faq-item">
                                        <div className="faq-question">{faq.q}</div>
                                        <div className="faq-answer">{faq.a}</div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="side-panel">
                            <div className="cta-box">
                                <h3>Get Immediate Help</h3>
                                <ul className="feature-list">
                                    {service.benefits.map((benefit, i) => (
                                        <li key={i}><span className="feature-icon">★</span> {benefit}</li>
                                    ))}
                                    <li><span className="feature-icon">★</span> Fastest 45m Response</li>
                                    <li><span className="feature-icon">★</span> Best Price Guaranteed</li>
                                </ul>
                                <div className="btn-container">
                                    <a href="tel:+447432387241" className="btn btn-call">
                                        <img src={phoneIcon} alt="phone" className="btn-icon-img" style={{ filter: 'brightness(0)' }} />
                                        Call Now: +44 7432 387241
                                    </a>
                                    <a href="https://wa.me/447432387241" target="_blank" rel="noopener noreferrer" className="btn btn-whatsapp">
                                        <img src={whatsappIcon} alt="whatsapp" className="btn-icon-img" style={{ filter: 'invert(1)' }} />
                                        Message on WhatsApp
                                    </a>
                                </div>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginTop: '10px' }}>
                                <div style={{ background: '#f8f9fa', padding: '15px', borderRadius: '15px', textAlign: 'center' }}>
                                    <div style={{ fontWeight: 800, fontSize: '20px' }}><AnimatedCounter end={1000} suffix="+" /></div>
                                    <div style={{ fontSize: '10px', textTransform: 'uppercase', color: '#666' }}>Happy Clients</div>
                                </div>
                                <div style={{ background: '#f8f9fa', padding: '15px', borderRadius: '15px', textAlign: 'center' }}>
                                    <div style={{ fontWeight: 800, fontSize: '20px' }}>24/7</div>
                                    <div style={{ fontSize: '10px', textTransform: 'uppercase', color: '#666' }}>Response</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>

            <footer style={{
                backgroundColor: '#333',
                color: 'white',
                padding: '40px 20px',
                textAlign: 'center'
            }}>
                <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '15px', marginBottom: '20px' }}>
                        <img
                            src="/my-logo.png"
                            alt="Cheapest Car Recovery UK Logo"
                            style={{ height: '50px', width: 'auto' }}
                        />
                        <h3 style={{ fontSize: '24px', margin: 0 }}>
                            Cheapest Car Recovery UK - Contact Us
                        </h3>
                    </div>
                    <div style={{ fontSize: '18px', lineHeight: '2', marginBottom: '20px' }}>
                        <div>📧 admin@cheapestcarrecovery.uk</div>
                        <div>+44 7432 387241</div>
                        <div>Serving the UK</div>
                    </div>
                    <p style={{ fontSize: '14px', color: '#ccc', marginTop: '20px' }}>
                        © 2024 Cheapest Car Recovery UK. Professional vehicle recovery services across the UK.
                        Available 24/7 for emergency breakdown recovery.
                    </p>
                </div>
            </footer>

            <div className="floating-buttons">
                <a
                    href="tel:+447432387241"
                    className="floating-btn call-btn"
                    title="Call Now"
                >
                    <img
                        src={phoneIcon}
                        alt="Phone"
                        className="floating-icon"
                    />
                    <span className="floating-btn-text">Call Now</span>
                </a>
                <a
                    href="https://wa.me/447432387241?text=Hi%2C%20I%20need%20car%20recovery%20service"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="floating-btn whatsapp-btn"
                    title="WhatsApp Chat"
                >
                    <img
                        src={whatsappIcon}
                        alt="WhatsApp"
                        className="floating-icon"
                    />
                    <span className="floating-btn-text">WhatsApp</span>
                </a>
            </div>
        </div>
    );
};

export default ServiceDetail;
